/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Tabs, Tab, Modal, Row, Col } from 'react-bootstrap';
import './ViewAllQuotations.css';
import SearchServices from '../features/SearchServices';
import PaginationComponent from '../features/Pagination';
import BASE_URL from '../config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPrint, FaEdit, FaEye, FaTrashAlt } from 'react-icons/fa';
import Invoice from './Invoice';
import ExtraInvoice from './ExtraInvoice';

const ViewAlLQuotations = () => {
  const [services, setServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage] = useState(5); // Number of customers per page
  const [servicesPerPage] = useState(5);
  const [selectedService, setSelectedService] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceType, setInvoiceType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [key, setKey] = useState('CASH');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${BASE_URL}/quotation`);
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
      const servicesData = await response.json();

      const servicesWithCustomers = await Promise.all(
        servicesData.map(async (service) => {
          try {
            if (service.customer_id) {
              const customerResponse = await fetch(`${BASE_URL}/customers/${service.customer_id}`);
              if (!customerResponse.ok) {
                throw new Error(`Failed to fetch customer for customer ID ${service.customer_id}`);
              }
              const customerData = await customerResponse.json();

              if (customerData.data && customerData.data.firstName && customerData.data.lastName) {
                const invoiceResponse = await fetch(`${BASE_URL}/quotation/${service.quotation_id}`);
                const invoiceData = await invoiceResponse.json();

                const extraInvoiceResponse = await fetch(`${BASE_URL}/quotation/updatesave/${service.quotation_id}`);
                const extraInvoiceData = await extraInvoiceResponse.json();

                return {
                  ...service,
                  customerName: `${customerData.data.title} ${customerData.data.firstName} ${customerData.data.lastName}`,
                  customerEmail: customerData.data.email || '',
                  customerContactNumber: customerData.data.contactNo || '',
                  invoice: invoiceData || {},
                  extraInvoice: extraInvoiceData || {},
                };
              } else {
                throw new Error(`Invalid customer data for customer ID ${service.customer_id}`);
              }
            } else {
              throw new Error(`Customer ID not found for service ID ${service._id}`);
            }
          } catch (error) {
            console.error('Error fetching customer data:', error);
            return {
              ...service,
              customerName: 'Unknown',
              customerEmail: 'Unknown',
              customerContactNumber: 'Unknown',
              invoice: {},
              extraInvoice: {},
            };
          }
        })
      );

      servicesWithCustomers.sort((a, b) => new Date(b.createdAtService) - new Date(a.createdAtService));

      setServices(servicesWithCustomers);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const filteredServices = services.filter(service => {
    const invoiceId = service.quotation_id || '';
    const customerName = service.customerName || '';
    const vehicleNumber = service.vehicle_no || '';
    
    const searchQuery = searchTerm.toLowerCase();
  
    return (
      invoiceId.toLowerCase().includes(searchQuery) ||
      customerName.toLowerCase().includes(searchQuery) ||
      vehicleNumber.toLowerCase().includes(searchQuery)
    );
  });

  const servicesByPaymentMethod = filteredServices.filter(service => {
    if (key === 'CASH') {
      return service.paymentType === 'CASH';
    } else if (key === 'CREDIT') {
      return service.paymentType === 'CREDIT';
    }
    return true;
  });

  // Logic to calculate current customers on current page
  const indexOfLastService = currentPage * customersPerPage;
  const indexOfFirstService = indexOfLastService - customersPerPage;
  const currentCustomers = filteredServices && filteredServices.slice(indexOfFirstService, indexOfLastService);

  // Ensure filteredCustomers is defined before calculating totalPages
  const totalPages = filteredServices ? Math.ceil(filteredServices.length / customersPerPage) : 0;

  const generateInvoice = (id, type) => {
    const service = services.find(service => service.quotation_id === id);
    if (service) {
      setSelectedService(service);
      setInvoiceType(type);
      setShowInvoiceModal(true);
    }
  };

  const handleCloseInvoice = () => {
    setSelectedService(null);
    setShowInvoiceModal(false);
    setInvoiceType('');
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleShowDeleteModal = (id) => {
    setServiceToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setServiceToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!serviceToDelete) return;
  
    try {
      const response = await fetch(`${BASE_URL}/quotation/delete/${serviceToDelete}`, {
        method: 'DELETE',
      });
  
      if (response.ok) {
        // Remove the deleted service from the list and re-calculate the total pages
        const updatedServices = services.filter(service => service.quotation_id !== serviceToDelete);
        setServices(updatedServices);
  
        // Adjust current page if needed after deletion
        const updatedTotalPages = Math.ceil(updatedServices.length / servicesPerPage);
        if (currentPage > updatedTotalPages) {
          setCurrentPage(updatedTotalPages); // Go to last available page if current one is now empty
        }
  
        toast.success('Quotation deleted successfully');
      } else {
        throw new Error('Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service. Please try again.');
    } finally {
      handleCloseDeleteModal();
    }
  };  

  return (
    <div className="view-all-services">
      <h2 className="text-center">All Quotations</h2>

      <Tabs
        id="controlled-tab-example"
        activeKey={key}
        onSelect={(k) => setKey(k)}
        className="mb-3"
      >
        <Tab eventKey="CASH" title="CASH">
          <SearchServices searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Quotation ID</th>
                <th>Vehicle Number</th>
                <th>Created Date</th>
                <th>Total Amount</th>
                <th>Customer Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
      {servicesByPaymentMethod
        .filter(service => service.paymentType === 'CASH')
        .slice(indexOfFirstService, indexOfLastService)
        .map(service => (
          <tr key={service.quotation_id}>
            <td>{service.quotation_id}</td>
            <td>{service.vehicle_no}</td>
            <td>{new Date(service.createdAtService).toLocaleString()}</td>
            <td>Rs. {service.totalAmount}.00</td>
            <td>{service.customerName}</td>
            <td>
              <Link to={`/update-quotation/${service.quotation_id}`} className="action-button">
                <Button variant="warning" className="mr-2"><FaEdit /></Button>
              </Link>
              <Link to={`/view-quotation/${service.quotation_id}`} className="action-button">
                <Button variant="primary" className="mr-2"><FaEye /></Button>
              </Link>
              {service.service_status === 'Completed' && (
                <>
                  <Button
                    variant="success"
                    onClick={() => generateInvoice(service.quotation_id, 'invoice')}
                    className="action-button"
                  >
                    <FaPrint className="mr-2" />
                  </Button>
                </>
              )}
              <Button
                variant="danger"
                onClick={() => handleShowDeleteModal(service.quotation_id)}
                className="action-button"
              >
                <FaTrashAlt />
              </Button>
            </td>
          </tr>
        ))}
    </tbody>
          </Table>
        </Tab>
        <Tab eventKey="CREDIT" title="CREDIT">
          <SearchServices searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Quotation ID</th>
                <th>Vehicle Number</th>
                <th>Created Date</th>
                <th>Total Amount</th>
                <th>Customer Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
      {servicesByPaymentMethod
        .filter(service => service.paymentType === 'CREDIT')
        .slice(indexOfFirstService, indexOfLastService)
        .map(service => (
          <tr key={service.quotation_id}>
            <td>{service.quotation_id}</td>
            <td>{service.vehicle_no}</td>
            <td>{new Date(service.createdAtService).toLocaleString()}</td>
            <td>Rs. {service.totalExtraAmount}.00</td>
            <td>{service.customerName}</td>
            <td>
              <Link to={`/update-quotation/${service.quotation_id}`} className="action-button">
                <Button variant="warning" className="mr-2"><FaEdit /></Button>
              </Link>
              <Link to={`/view-quotation/${service.quotation_id}`} className="action-button">
                <Button variant="primary" className="mr-2"><FaEye /></Button>
              </Link>
              {service.service_status === 'Completed' && (
                <>
                  <Button
                    variant="success"
                    onClick={() => generateInvoice(service.quotation_id, 'invoice')}
                    className="action-button"
                  >
                    <FaPrint className="mr-2" />
                  </Button>
                </>
              )}
              <Button
                variant="danger"
                onClick={() => handleShowDeleteModal(service.quotation_id)}
                className="action-button"
              >
                <FaTrashAlt />
              </Button>
            </td>
          </tr>
        ))}
    </tbody>
          </Table>
        </Tab>
      </Tabs>

      <Row className="justify-content-center">
        <Col xs="auto">
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Col>
      </Row>

      {selectedService && (
        <Modal show={showInvoiceModal} onHide={handleCloseInvoice} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Invoice</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {invoiceType === 'invoice' ? (
              <Invoice service={selectedService} />
            ) : (
              <ExtraInvoice service={selectedService} />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseInvoice}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this service?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <Invoice
        show={showInvoiceModal && invoiceType === 'invoice'}
        handleClose={handleCloseInvoice}
        service={selectedService}
      />
      {/* <ExtraInvoice
        show={showInvoiceModal && invoiceType === 'extraInvoice'}
        handleClose={handleCloseInvoice}
        service={selectedService}
      /> */}
    </div>
  );
};

export default ViewAlLQuotations;